const translation = {
  common: {
    submit: 'Finish',
    back: 'Back',
    ok: 'OK',
    save: 'Save',
    cancel: 'Cancel',
    send: 'Send',
    next: 'Next',
    advance: 'Continue',
    yes: 'Yes',
    notApplicable: 'Not applicable',
    patient: 'Patient',
    inProgress: 'in progress',
    hospitalLogoAlt: 'Hospital logo',
  },
  validation: {
    required: 'Required field',
    cpfInvalid: 'Invalid CPF',
    selectAtLeastOneItem: 'You must select at least one item!',
    mustSelectOneItem: 'You need to select at least one item!',
    integerQuantityOnly: 'Only whole quantities can be requested!',
    quantityLessThan: 'The quantity must be less than {{max}}.',
    quantityGreaterThan: 'The quantity must be greater than {{min}}.',
    quantityBetween: 'The quantity must be between {{min}} and {{max}}.',
  },
  language: {
    selector: {
      open: 'Select language',
    },
    modal: {
      title: 'Language selector',
      subtitle: 'Select your preferred language.',
      save: 'Save',
    },
  },
  welcome: {
    title: 'Welcome!',
    body: 'To start using the app, we need you to confirm whether you are the <strong>patient</strong> or a <strong>companion</strong>.',
    bodyExtra:
      'We use this information to make sure you have the best experience!',
    userType: {
      patient: "I'm the patient",
      companion: "I'm a companion",
    },
  },
  login: {
    pageTitle: 'Confirm your identity',
    illustrationAlt: 'Illustration of healthcare professionals ready to help',
    almostThere: 'Almost there...',
    companionInstructions:
      'To access the system, please enter your name and the <strong>admission number</strong> located on the identification wristband of the person being accompanied, or the <strong>access code</strong> located below the QR Code, as instructed by the hospital staff.',
    patientInstructions:
      'To access the system, please enter your <strong>admission number</strong>, located on your identification wristband, or the <strong>access code</strong> located below the QR Code, as instructed by the hospital staff.',
    fields: {
      name: 'Your name',
      admission: 'Admission number',
      companionBirthDate: "What is the patient's date of birth?",
      patientBirthDate: 'What is your date of birth?',
    },
  },
  home: {
    pageTitle: 'Home - Quality24',
    heading: 'How can we help?',
    cards: {
      nurse: 'Call nursing',
      infrastructure: 'Request {{service}}',
      feedbackFallback: 'Compliments and complaints',
    },
  },
  greeting: {
    morning: 'Good morning',
    afternoon: 'Good afternoon',
    evening: 'Good evening',
  },
  nav: {
    home: 'Home',
    orders: 'My requests',
  },
  errors: {
    loadAdmission:
      "We couldn't load your admission. Please check your details and internet connection.",
    requestFailed: "We couldn't complete your request. Please try again.",
    surveyRequestFailed: "We couldn't complete your request. Please try again.",
    coverageCheckFailed:
      "We couldn't verify your admission's coverage. Please try again.",
    cancelOrderFailed:
      'Failed to cancel the request. Please try again. If the error persists, contact support.',
    genericRequest: 'There was an error with the request. Please try again.',
    serviceUnavailableAtThisTime:
      'This service category is not available at this time.',
  },
  payment: {
    status: {
      awaitingPayment: 'Awaiting payment',
      paid: 'Paid',
      denied: 'Denied',
      expired: 'Expired',
      voided: 'Voided',
      notCompleted: 'Not completed',
      authorized: 'Authorized',
      refund: 'Refund',
    },
  },
  pain: {
    none: 'No pain',
    mild: 'Mild',
    moderate: 'Moderate',
    severe: 'Severe',
    verySevere: 'Very severe',
    worst: 'Worst possible pain',
  },
  rating: {
    terrible: 'Terrible',
    bad: 'Bad',
    regular: 'Average',
    good: 'Good',
    great: 'Great',
  },
  maintenance: {
    title: 'This service is under maintenance.',
    body: 'Please wait to use it.',
  },
  survey: {
    improvements: {
      question: 'In your opinion, which of the items below could be improved?',
      subtitle: 'Select as many as you find necessary.',
    },
    nav: {
      progress: 'Survey progress',
    },
    notification: {
      text: 'How about telling us <strong>how your experience was</strong> this {{day}}?',
      cta: 'tap to answer',
    },
    comment: {
      label: 'Leave your comment below:',
    },
    success: 'Thank you very much! Your evaluation was received successfully.',
  },
  dailySurvey: {
    pageTitle: 'Daily Survey - Quality24',
    confirmFinishTitle:
      'Are you sure you want to finish and submit this evaluation?',
    noAnswersTitle: "You haven't answered any question!",
    cancelEvaluation: 'Cancel evaluation',
    reviewQuestions: 'Review questions',
    confirmFinishBody:
      'If you want to review or change any answer, tap "Cancel". Otherwise, tap "Send".',
    noAnswersBody:
      'Would you like to cancel this evaluation or review the questions?',
    respondNow: 'Answer now',
    respondLater: 'Answer later',
    declineToAnswer: "I don't want to answer",
  },
  callEvaluation: {
    evaluate: 'Rate',
    title: 'Rate your request',
    message:
      'What rating would you give your experience during the resolution of this request?',
    comment: 'Comment',
    skip: "I don't want to rate",
  },
  alerts: {
    noActive: 'There are no active requests',
    noResolved: 'There are no resolved requests',
    viewDetails: 'View details',
    resolvedAt: 'Resolved at {{time}}',
    responsible: 'Handled by: {{name}}',
    cancelledToast:
      'Your request for "{{reason}}" was cancelled with the reason "{{cancelReason}}".',
    notInformed: 'Not informed',
  },
  order: {
    category: {
      nursing: 'nursing',
    },
    section: {
      request: 'Request {{category}}',
      selectOptionToOpen: 'Select an option below to open the request',
    },
    createRequest: 'Create request',
    selectOption: 'Select an option',
    openCall: 'Open request',
    fillBelow: 'Fill in below',
    selectReason: 'Select one of the reasons below:',
    details: {
      pageTitle: 'Request details - Quality24',
      /** Joins a localized date and time — `L` and `LTS` in moment */
      dateAtTime: '{{date}} at {{time}}',
      date: 'Date',
      placed: 'Order placed',
      paymentApproved: 'Payment approved',
      inProgress: 'In progress',
      cancelled: 'Cancelled',
      closed: 'Closed',
      order: 'Order',
      items: 'Order items',
      total: 'Total',
      totalValue: 'Total value',
      cancelOrder: 'Cancel order',
      payNow: 'Pay now',
    },
    tracking: {
      pageTitle: 'Requests - Quality24',
      open: 'Open ({{count}})',
      finished: 'Finished ({{count}})',
    },
    rating: {
      pageTitle: 'Request rating - Quality24',
      messageWithResolver:
        "How do you rate your experience resolving the request for '{{reason}}', resolved by {{resolver}}?",
      message:
        "How do you rate your experience resolving the request for '{{reason}}'?",
    },
  },
  hospitalInfo: {
    additionalInfo: 'Additional information',
    learnMore: 'Learn more',
    pageTitle: 'General information - Quality24',
  },
  customData: {
    generalInfo: 'General information',
    otherServices: 'Other services',
  },
  integration: {
    notFound: 'The provided admission was not found.',
    loading: 'Please wait while we load your admission...',
  },
  lgpd: {
    consentSuccess: 'You have agreed to the data protection terms. Welcome!',
    agree: 'Agree',
    refuse: 'Decline',
    refuseAndLogout: 'Decline and log out',
    confirmRefuse:
      'Are you sure? By declining the terms, you will be logged out of the app.',
  },
} as const;

export default translation;
